import { Pipe, PipeTransform, inject } from '@angular/core';
import { LocalizationDisplayService } from 'src/core/services/settings/localization-display.service';

/**
 * Renders a timestamp with both the tenant's Date Format and Time Format preferences
 * (Administration &gt; Settings &gt; Localization), e.g. `06/08/2026 14:35`.
 *
 * Pass `true` to keep second precision (`06/08/2026 14:35:07`).
 *
 * ```html
 * {{ call.startedAt | displayDateTime }}
 * {{ log.timestampUtc | displayDateTime: true }}
 * ```
 */
@Pipe({
  name: 'displayDateTime',
  standalone: true,
  pure: false,
})
export class DisplayDateTimePipe implements PipeTransform {
  private readonly localizationDisplay = inject(LocalizationDisplayService);

  private cachedInput: Date | string | number | null | undefined;
  private cachedWithSeconds = false;
  private cachedTimezone: string | undefined;
  private cachedSettingsHash = '';
  private cachedResult: string | null = null;

  transform(
    value: Date | string | number | null | undefined,
    withSeconds = false,
    timezone?: string
  ): string | null {
    const settingsHash = this.localizationDisplay.settingsHash;

    if (
      value === this.cachedInput &&
      withSeconds === this.cachedWithSeconds &&
      timezone === this.cachedTimezone &&
      settingsHash === this.cachedSettingsHash
    ) {
      return this.cachedResult;
    }

    this.cachedInput = value;
    this.cachedWithSeconds = withSeconds;
    this.cachedTimezone = timezone;
    this.cachedSettingsHash = settingsHash;
    this.cachedResult = this.localizationDisplay.formatDateTime(value, withSeconds, timezone);

    return this.cachedResult;
  }
}
